import { useCallback, useId, useLayoutEffect, useRef, useState } from 'react'

import { Icon } from '../Icon'

/**
 * Пара снимков «до/после» с тремя режимами сравнения.
 *
 * ── Почему три режима, а не один ──────────────────────────────────
 *
 * У диспетчера три разные задачи. «Рядом» — быстро понять, тот же
 * это ракурс вообще. «Наложение» — поймать разницу в одном и том же
 * месте кузова. «Крупно» — рассмотреть царапину, которой на
 * миниатюре просто не видно. Это главная жалоба на фотоприёмку
 * вообще: спор разваливается на снимке, где ничего не разобрать.
 *
 * ── Оба снимка живут в одной коробке ──────────────────────────────
 *
 * Водитель снимает телефоном как придётся: приёмка вертикальная,
 * сдача горизонтальная. Если дать каждому снимку свои пропорции,
 * наложение перестаёт что-либо значить — сравниваются разные части
 * кадра. Поэтому коробка одна и общая, а снимок вписывается в неё
 * целиком (object-fit: contain). Кадр не обрезается: обрезанный угол
 * бампера — это ровно то место, из-за которого потом идёт спор.
 *
 * ── Слайдер наложения ─────────────────────────────────────────────
 *
 * Мышь, тач и перо обрабатываются одним путём через Pointer Events.
 * Клавиатура — не «тоже поддержана», а полноценный путь: ручка
 * объявлена как role="slider", стрелки двигают границу, Home и End
 * прижимают её к краям.
 *
 * Движение берётся из токенов длительности, а те под
 * prefers-reduced-motion обнуляются — отдельной ветки для
 * уменьшенного движения здесь нет намеренно, чтобы правило жило
 * в одном месте.
 *
 * ── Позиция границы едет в CSS одним числом ───────────────────────
 *
 * Инлайн-стили в пакетах запрещены, и правильно: примитив, который
 * рисует себя сам, невозможно перекрасить из темы. Но положение
 * границы — это геометрия, меняющаяся непрерывно, и в статическом
 * классе её не выразить. Через границу пакета проходит ровно одно
 * число в переменной --compare-pos; что именно оно двигает — вырез
 * верхнего слоя и саму ручку — решает таблица стилей.
 */

const MODES = [
  { key: 'side', label: 'Рядом', icon: 'columns' },
  { key: 'overlay', label: 'Наложение', icon: 'overlay' },
  { key: 'large', label: 'Крупно', icon: 'expand' },
]

const STEP = 2
const BIG_STEP = 10

function clampPercent(value) {
  return Math.min(100, Math.max(0, value))
}

function Shot({ photo, caption, empty }) {
  if (!photo?.url) {
    return (
      <div className="image-compare__shot image-compare__shot--empty">
        <Icon name="camera" size={20} />
        <span>{empty}</span>
      </div>
    )
  }
  return (
    <figure className="image-compare__shot">
      <img src={photo.url} alt={caption} loading="lazy" />
      <figcaption className="image-compare__caption">
        <span>{caption}</span>
        {photo.takenAt && (
          <time className="image-compare__time" dateTime={photo.takenAt}>
            {photo.takenAtLabel || photo.takenAt}
          </time>
        )}
      </figcaption>
    </figure>
  )
}

export function ImageCompare({
  title,
  /** { url, takenAt, takenAtLabel } — снимок приёмки */
  before = null,
  /** тот же вид — снимок сдачи */
  after = null,
  beforeLabel = 'Приёмка',
  afterLabel = 'Сдача',
  defaultMode = 'side',
  onModeChange,
}) {
  const bothPresent = Boolean(before?.url && after?.url)
  // Накладывать нечего, если одной стороны нет: показываем то, что есть.
  const [mode, setMode] = useState(
    defaultMode === 'overlay' && !bothPresent ? 'side' : defaultMode
  )
  const [position, setPosition] = useState(50)
  const [dragging, setDragging] = useState(false)
  const boxRef = useRef(null)
  const headingId = useId()

  const effectiveMode = mode === 'overlay' && !bothPresent ? 'side' : mode

  // Пишем до отрисовки: в useEffect кадр успел бы показать границу
  // на старом месте, и при перетаскивании она бы отставала.
  useLayoutEffect(() => {
    boxRef.current?.style.setProperty('--compare-pos', `${position}%`)
  }, [position, effectiveMode])

  const changeMode = (next) => {
    setMode(next)
    onModeChange?.(next)
  }

  const positionFromEvent = useCallback((clientX) => {
    const rect = boxRef.current?.getBoundingClientRect()
    if (!rect?.width) return null
    return clampPercent(((clientX - rect.left) / rect.width) * 100)
  }, [])

  const onPointerDown = (e) => {
    const next = positionFromEvent(e.clientX)
    if (next === null) return
    setDragging(true)
    setPosition(next)
    // Захват указателя: палец или курсор может уйти за пределы кадра,
    // и без захвата перетаскивание оборвётся на границе.
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }

  const onPointerMove = (e) => {
    if (!dragging) return
    const next = positionFromEvent(e.clientX)
    if (next !== null) setPosition(next)
  }

  const endDrag = (e) => {
    if (!dragging) return
    setDragging(false)
    e.currentTarget.releasePointerCapture?.(e.pointerId)
  }

  const onKeyDown = (e) => {
    const moves = {
      ArrowLeft: -STEP,
      ArrowRight: STEP,
      ArrowDown: -STEP,
      ArrowUp: STEP,
      PageDown: -BIG_STEP,
      PageUp: BIG_STEP,
    }
    if (e.key in moves) {
      e.preventDefault()
      setPosition((p) => clampPercent(p + moves[e.key]))
      return
    }
    if (e.key === 'Home') {
      e.preventDefault()
      setPosition(0)
    }
    if (e.key === 'End') {
      e.preventDefault()
      setPosition(100)
    }
  }

  return (
    <div
      className={`image-compare image-compare--${effectiveMode}`}
      aria-labelledby={headingId}
      role="group"
    >
      <div className="image-compare__head">
        <h4 className="image-compare__title" id={headingId}>{title}</h4>

        <div className="image-compare__modes" role="tablist" aria-label="Режим сравнения">
          {MODES.map((m) => {
            const disabled = m.key === 'overlay' && !bothPresent
            return (
              <button
                key={m.key}
                type="button"
                role="tab"
                aria-selected={m.key === effectiveMode}
                className={`image-compare__mode${m.key === effectiveMode ? ' is-active' : ''}`}
                onClick={() => changeMode(m.key)}
                disabled={disabled}
                title={disabled ? 'Нужны оба снимка' : undefined}
              >
                <Icon name={m.icon} size={13} />
                {m.label}
              </button>
            )
          })}
        </div>
      </div>

      {effectiveMode === 'overlay' ? (
        <div
          className={`image-compare__overlay${dragging ? ' is-dragging' : ''}`}
          ref={boxRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          <img className="image-compare__layer" src={before.url} alt={beforeLabel} />
          <img
            className="image-compare__layer image-compare__layer--after"
            src={after.url}
            alt={afterLabel}
          />

          {/* Время съёмки видно и здесь: в споре о повреждении именно
              оно отвечает, когда снимок сделан, и прятать его в одном
              из режимов значит прятать доказательство. */}
          <span className="image-compare__tag image-compare__tag--left">
            {beforeLabel}
            {before.takenAtLabel && <time dateTime={before.takenAt}>{before.takenAtLabel}</time>}
          </span>
          <span className="image-compare__tag image-compare__tag--right">
            {afterLabel}
            {after.takenAtLabel && <time dateTime={after.takenAt}>{after.takenAtLabel}</time>}
          </span>

          <div
            className="image-compare__handle"
            role="slider"
            tabIndex={0}
            aria-label={`Граница сравнения: ${beforeLabel} и ${afterLabel}`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(position)}
            aria-valuetext={`${Math.round(position)}% ${afterLabel}`}
            onKeyDown={onKeyDown}
          >
            <span className="image-compare__grip" aria-hidden="true" />
          </div>
        </div>
      ) : (
        <div className="image-compare__pair">
          <Shot photo={before} caption={beforeLabel} empty="Снимка приёмки нет" />
          <Shot photo={after} caption={afterLabel} empty="Снимка сдачи нет" />
        </div>
      )}
    </div>
  )
}
